/* ==========================================================================
   COVVE — theme behaviour
   Vanilla, no dependencies. Each module is independent and no-ops when its
   markup is absent, so sections can be added or removed freely.
   ========================================================================== */
(function () {
  'use strict';

  // -- config -------------------------------------------------------------

  var CFG = { routes: {}, cart: {}, strings: {} };
  try {
    var raw = document.getElementById('covve-config');
    if (raw) CFG = JSON.parse(raw.textContent);
  } catch (e) {
    console.warn('[covve] config unreadable', e);
  }

  var $ = function (sel, root) { return (root || document).querySelector(sel); };
  var $$ = function (sel, root) {
    return Array.prototype.slice.call((root || document).querySelectorAll(sel));
  };
  var on = function (el, ev, fn, opts) { if (el) el.addEventListener(ev, fn, opts); };

  // -- scroll lock --------------------------------------------------------
  // Drawers and the search overlay stack; only release when the last closes.

  var lockCount = 0;
  var savedScrollY = 0;

  function lockScroll() {
    if (lockCount === 0) {
      savedScrollY = window.scrollY;
      document.body.style.position = 'fixed';
      document.body.style.top = -savedScrollY + 'px';
      document.body.style.width = '100%';
    }
    lockCount++;
  }

  function unlockScroll() {
    lockCount = Math.max(0, lockCount - 1);
    if (lockCount === 0) {
      document.body.style.position = '';
      document.body.style.top = '';
      document.body.style.width = '';
      window.scrollTo(0, savedScrollY);
    }
  }

  // -- focus management ---------------------------------------------------

  var FOCUSABLE = 'a[href],button:not([disabled]),input:not([disabled]),select,textarea,[tabindex]:not([tabindex="-1"])';

  function trapFocus(container, opener) {
    var previous = opener || document.activeElement;

    function keydown(e) {
      if (e.key !== 'Tab') return;
      var items = $$(FOCUSABLE, container).filter(function (el) {
        return el.offsetParent !== null;
      });
      if (!items.length) return;
      var first = items[0];
      var last = items[items.length - 1];
      if (e.shiftKey && document.activeElement === first) {
        e.preventDefault(); last.focus();
      } else if (!e.shiftKey && document.activeElement === last) {
        e.preventDefault(); first.focus();
      }
    }

    container.addEventListener('keydown', keydown);
    var firstFocusable = $$(FOCUSABLE, container).filter(function (el) {
      return el.offsetParent !== null;
    })[0];
    if (firstFocusable) firstFocusable.focus();

    return function release() {
      container.removeEventListener('keydown', keydown);
      if (previous && previous.focus) previous.focus();
    };
  }

  // -- overlay controller -------------------------------------------------

  function makeOverlay(el, opts) {
    if (!el) return null;
    opts = opts || {};
    var release = null;

    // A trigger that is a plain link until JS upgrades it shouldn't advertise
    // aria-expanded in the server-rendered HTML — set it when we take over.
    function enhanceTriggers() {
      $$('[aria-controls="' + el.id + '"]').forEach(function (b) {
        if (!b.hasAttribute('aria-expanded')) b.setAttribute('aria-expanded', 'false');
      });
    }

    function open(trigger) {
      if (!el.hidden) return;
      el.hidden = false;
      lockScroll();
      release = trapFocus(el, trigger);
      $$('[aria-controls="' + el.id + '"]').forEach(function (b) {
        b.setAttribute('aria-expanded', 'true');
      });
      if (opts.onOpen) opts.onOpen();
    }

    function close() {
      if (el.hidden) return;
      el.hidden = true;
      unlockScroll();
      if (release) { release(); release = null; }
      $$('[aria-controls="' + el.id + '"]').forEach(function (b) {
        b.setAttribute('aria-expanded', 'false');
      });
      if (opts.onClose) opts.onClose();
    }

    on(document, 'keydown', function (e) {
      if (e.key === 'Escape' && !el.hidden) close();
    });

    return {
      el: el,
      open: open,
      close: close,
      enhanceTriggers: enhanceTriggers,
      isOpen: function () { return !el.hidden; }
    };
  }

  // ======================================================================
  // Header — scroll-driven transform over the homepage hero.
  // The arithmetic below is transcribed from the mockup so the motion matches.
  // ======================================================================

  (function header() {
    var el = $('[data-header]');
    if (!el) return;

    var logo = $('[data-logo]', el);
    var hero = $('[data-hero-under-header]');
    var overHero = el.hasAttribute('data-over-hero');
    var RANGE = parseFloat(el.getAttribute('data-scroll-range')) || 340;

    if (!overHero) {
      // Resting state comes from CSS defaults; nothing to drive.
      return;
    }

    var last = -1;
    var queued = false;

    // The hero expansion scales the wordmark to 3.3x. That only fits where
    // there is room for it — at 375px it is roughly three viewports wide and
    // collides with every header icon. Below this width the header stays at
    // its resting 66px and the logo is left alone.
    var EXPAND_MIN_WIDTH = 860;
    var expanded = false;

    function reset() {
      var s = el.style;
      ['--header-height', '--header-fade', '--header-ink', '--header-border',
       '--header-ink-shadow', '--header-icon-shadow'].forEach(function (prop) {
        s.removeProperty(prop);
      });
      if (logo) {
        logo.style.removeProperty('--logo-size');
        logo.style.removeProperty('--logo-tracking');
      }
      if (hero) hero.style.setProperty('--hero-offset', '0px');
      expanded = false;
      last = -1;
    }

    function paint() {
      queued = false;

      if (window.innerWidth < EXPAND_MIN_WIDTH) {
        if (expanded || last !== -1) reset();
        return;
      }
      expanded = true;

      var scroller = document.scrollingElement || document.documentElement;
      var y = scroller ? scroller.scrollTop : 0;
      var p = Math.min(1, Math.max(0, y / RANGE));

      // Skip sub-pixel churn, but always settle exactly on the endpoint.
      if (Math.abs(p - last) <= 0.004 && (p === 1) === (last === 1)) return;
      last = p;

      var ease = 1 - Math.pow(1 - p, 2);
      var fade = Math.min(1, Math.max(0, (p - 0.62) / 0.28));
      var height = Math.round(66 + 78 * (1 - ease));

      var s = el.style;
      s.setProperty('--header-height', height + 'px');
      s.setProperty('--header-fade', fade.toFixed(3));
      s.setProperty('--header-ink',
        'rgb(' + Math.round(255 - 235 * fade) + ',' +
                 Math.round(255 - 236 * fade) + ',' +
                 Math.round(255 - 238 * fade) + ')');
      s.setProperty('--header-border', 'rgba(220,216,208,' + fade.toFixed(3) + ')');
      s.setProperty('--header-ink-shadow',
        fade > 0.98 ? 'none' : '0 1px 14px rgba(0,0,0,' + (0.55 * (1 - fade)).toFixed(3) + ')');
      s.setProperty('--header-icon-shadow',
        fade > 0.98 ? 'none' : 'drop-shadow(0 1px 6px rgba(0,0,0,' + (0.6 * (1 - fade)).toFixed(3) + '))');

      if (logo) {
        // 27px x 3.3 = 89.1px at the top of the hero, easing down to 27px.
        // Same geometry the mockup got from scale(3.3), but rendered not zoomed.
        logo.style.setProperty('--logo-size', (27 * (3.3 - 2.3 * ease)).toFixed(2) + 'px');
        logo.style.setProperty('--logo-tracking', (0.34 - 0.1 * p).toFixed(3) + 'em');
      }

      if (hero) hero.style.setProperty('--hero-offset', '-' + height + 'px');
    }

    // Coalesce to one paint per frame, and only while something is moving.
    // The mockup polled every frame forever; on a real storefront that keeps
    // the main thread awake for as long as the page is open.
    function request() {
      if (queued) return;
      queued = true;
      requestAnimationFrame(paint);
    }

    paint(); // first paint is synchronous so there is no flash
    on(window, 'scroll', request, { passive: true });
    on(window, 'resize', function () { last = -1; request(); });
    on(window, 'pageshow', function () { last = -1; request(); });
  })();

  // ======================================================================
  // Announcement bar — rotating messages
  // ======================================================================

  (function announcement() {
    var bar = $('[data-announcement]');
    if (!bar) return;

    var messages = $$('.announcement__msg', bar);
    if (!messages.length) return;

    var i = 0;
    var interval = parseInt(bar.getAttribute('data-interval'), 10) || 5000;
    var timer = null;

    function show(next) {
      messages[i].classList.remove('is-active');
      i = (next + messages.length) % messages.length;
      messages[i].classList.add('is-active');
    }

    function start() {
      if (messages.length < 2) return;
      stop();
      timer = setInterval(function () { show(i + 1); }, interval);
    }
    function stop() { if (timer) { clearInterval(timer); timer = null; } }

    on($('[data-ann-next]', bar), 'click', function () { show(i + 1); start(); });
    on($('[data-ann-prev]', bar), 'click', function () { show(i - 1); start(); });

    on($('[data-ann-close]', bar), 'click', function () {
      stop();
      bar.hidden = true;
      try { sessionStorage.setItem('covve:ann-dismissed', '1'); } catch (e) {}
    });

    try {
      if (sessionStorage.getItem('covve:ann-dismissed') === '1') {
        bar.hidden = true;
        return;
      }
    } catch (e) {}

    // Pause while hovered or focused, and while the tab is in the background.
    on(bar, 'mouseenter', stop);
    on(bar, 'mouseleave', start);
    on(bar, 'focusin', stop);
    on(bar, 'focusout', start);
    on(document, 'visibilitychange', function () {
      if (document.hidden) stop(); else start();
    });

    start();
  })();

  // ======================================================================
  // Navigation drawer — nested panels
  // ======================================================================

  var navDrawer = (function () {
    var el = $('[data-nav-drawer]');
    if (!el) return null;

    var overlay = makeOverlay(el, { onClose: reset });
    var backBtn = $('[data-nav-back]', el);
    var backLabel = $('[data-nav-back-label]', el);
    var stack = [];

    function panel(name) { return $('[data-panel="' + name + '"]', el); }

    function activate(name) {
      $$('.drawer__level', el).forEach(function (p) { p.classList.remove('is-active'); });
      var target = panel(name);
      if (target) target.classList.add('is-active');

      if (stack.length) {
        var current = panel(stack[stack.length - 1]);
        // The mockup labels the button with the level you are currently in.
        backLabel.textContent = current ? current.getAttribute('data-title') : '';
        backBtn.hidden = false;
      } else {
        backBtn.hidden = true;
      }
    }

    function reset() {
      stack = [];
      activate('root');
    }

    $$('[data-open-panel]', el).forEach(function (btn) {
      on(btn, 'click', function () {
        var name = btn.getAttribute('data-open-panel');
        stack.push(name);
        activate(name);
        var list = $('.drawer__list', panel(name));
        if (list) list.scrollTop = 0;
      });
    });

    on(backBtn, 'click', function () {
      stack.pop();
      activate(stack.length ? stack[stack.length - 1] : 'root');
    });

    $$('[data-open-nav]').forEach(function (b) {
      on(b, 'click', function () { overlay.open(b); });
    });
    $$('[data-close-nav]', el).forEach(function (b) {
      on(b, 'click', overlay.close);
    });

    return overlay;
  })();

  // ======================================================================
  // Search overlay — Predictive Search
  // ======================================================================

  (function search() {
    var el = $('[data-search-overlay]');
    if (!el) return;

    var overlay = makeOverlay(el, {
      onOpen: function () { setTimeout(function () { if (input) input.focus(); }, 20); }
    });
    var input = $('[data-search-input]', el);
    var results = $('[data-search-results]', el);
    var countEl = $('[data-search-count]', el);
    var timer = null;
    var controller = null;

    overlay.enhanceTriggers();

    $$('[data-open-search]').forEach(function (b) {
      on(b, 'click', function (e) {
        // The trigger is a link to /search so it works without JS; take over here.
        e.preventDefault();
        if (navDrawer && navDrawer.isOpen()) navDrawer.close();
        overlay.open(b);
      });
    });
    $$('[data-close-search]', el).forEach(function (b) {
      on(b, 'click', overlay.close);
    });

    // Clicking the dimmed area closes; clicking the white panel does not.
    on(el, 'mousedown', function (e) {
      if (e.target === el) overlay.close();
    });

    function render(html) {
      var tmp = document.createElement('div');
      tmp.innerHTML = html;
      var root = $('[data-predictive-root]', tmp);
      if (!root) return;
      var label = $('[data-count-label]', root);
      var grid = $('.product-grid', root);
      countEl.textContent = label ? label.textContent.trim() : '';
      results.innerHTML = grid ? grid.innerHTML : '';
    }

    function query(term) {
      if (!term) {
        results.innerHTML = '';
        countEl.textContent = '';
        return;
      }
      if (controller) controller.abort();
      controller = typeof AbortController !== 'undefined' ? new AbortController() : null;

      var url = CFG.routes.predictive_search + '?q=' + encodeURIComponent(term) +
                '&resources[type]=product&resources[limit]=5' +
                '&section_id=predictive-search';

      fetch(url, controller ? { signal: controller.signal } : undefined)
        .then(function (r) { return r.ok ? r.text() : Promise.reject(r.status); })
        .then(render)
        .catch(function (err) {
          if (err && err.name === 'AbortError') return;
          console.warn('[covve] predictive search failed', err);
        });
    }

    on(input, 'input', function () {
      clearTimeout(timer);
      var term = input.value.trim();
      timer = setTimeout(function () { query(term); }, 220);
    });
  })();

  // ======================================================================
  // Country / localisation popover
  // ======================================================================

  (function country() {
    var pop = $('[data-country-pop]');
    if (!pop) return;

    function toggle(force) {
      var next = typeof force === 'boolean' ? force : pop.hidden;
      pop.hidden = !next;
    }

    $$('[data-open-country]').forEach(function (b) {
      on(b, 'click', function (e) {
        e.stopPropagation();
        if (navDrawer && navDrawer.isOpen()) navDrawer.close();
        toggle(true);
      });
    });

    on(document, 'click', function (e) {
      if (pop.hidden) return;
      var t = e.target;
      var inTrigger = t && t.closest && t.closest('[data-open-country]');
      if (!pop.contains(t) && !inTrigger) toggle(false);
    });

    on(document, 'keydown', function (e) {
      if (e.key === 'Escape') toggle(false);
    });
  })();

  // ======================================================================
  // Cart
  // ======================================================================

  var cart = (function () {
    var el = $('[data-cart-drawer]');
    var overlay = el ? makeOverlay(el) : null;

    if (CFG.cart.drawer && overlay) overlay.enhanceTriggers();

    $$('[data-open-cart]').forEach(function (b) {
      on(b, 'click', function (e) {
        // Anchor to /cart by default; only intercept when the drawer is on.
        if (!CFG.cart.drawer || !overlay) return;
        e.preventDefault();
        overlay.open(b);
      });
    });

    function bind(root) {
      if (!root) return;
      $$('[data-close-cart]', root).forEach(function (b) {
        on(b, 'click', function () { if (overlay) overlay.close(); });
      });
      $$('[data-cart-line]', root).forEach(function (line) {
        var key = line.getAttribute('data-line-key');
        var value = $('[data-qty-value]', line);
        var qty = parseInt(value ? value.textContent : '1', 10) || 1;
        on($('[data-qty-up]', line), 'click', function () { change(key, qty + 1); });
        on($('[data-qty-down]', line), 'click', function () { change(key, qty - 1); });
        on($('[data-line-remove]', line), 'click', function () { change(key, 0); });
      });
    }

    function setBusy(state) {
      var lines = $('[data-cart-lines]');
      if (lines) lines.classList.toggle('is-busy', state);
    }

    function refresh(sections) {
      // Swap the drawer markup and header count from a Section Rendering response.
      if (sections && sections['cart-drawer']) {
        var tmp = document.createElement('div');
        tmp.innerHTML = sections['cart-drawer'];
        var fresh = $('[data-cart-drawer]', tmp);
        if (fresh && el) {
          var wasOpen = !el.hidden;
          el.innerHTML = fresh.innerHTML;
          el.hidden = !wasOpen;
          bind(el);
        }
      }
      return fetch(CFG.routes.cart + '.js', { headers: { Accept: 'application/json' } })
        .then(function (r) { return r.json(); })
        .then(function (data) {
          $$('[data-cart-count]').forEach(function (n) { n.textContent = data.item_count; });
          return data;
        })
        .catch(function () { /* count is cosmetic; ignore */ });
    }

    function change(key, quantity) {
      setBusy(true);
      return fetch(CFG.routes.cart_change, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ id: key, quantity: quantity, sections: 'cart-drawer' })
      })
        .then(function (r) { return r.json(); })
        .then(function (data) { return refresh(data.sections); })
        .catch(function (err) {
          console.warn('[covve] cart change failed', err);
          window.location.href = CFG.routes.cart;
        })
        .then(function () { setBusy(false); });
    }

    function add(id, quantity) {
      return fetch(CFG.routes.cart_add, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({
          items: [{ id: id, quantity: quantity || 1 }],
          sections: 'cart-drawer'
        })
      }).then(function (r) {
        return r.json().then(function (data) {
          if (!r.ok) throw new Error(data.description || data.message || CFG.strings.cart_error);
          return data;
        });
      }).then(function (data) {
        return refresh(data.sections).then(function () {
          if (CFG.cart.drawer && overlay) overlay.open();
        });
      });
    }

    bind(el);
    return { add: add, change: change, open: function () { if (overlay) overlay.open(); } };
  })();

  // ======================================================================
  // Product cards — swatch swaps the image
  // ======================================================================

  (function cards() {
    function bind(root) {
      $$('[data-product-card]', root).forEach(function (card) {
        var base = $('[data-card-base]', card);
        var swatches = $$('[data-card-swatch]', card);
        if (!base || !swatches.length) return;

        swatches.forEach(function (sw) {
          on(sw, 'click', function (e) {
            e.preventDefault();
            e.stopPropagation();

            swatches.forEach(function (o) { o.setAttribute('aria-pressed', 'false'); });
            sw.setAttribute('aria-pressed', 'true');

            var img = sw.getAttribute('data-swatch-image');
            var fit = sw.getAttribute('data-swatch-fit');
            if (img) {
              base.style.backgroundImage = 'url(' + img + ')';
              if (fit) base.style.backgroundSize = fit;
            }

            // Point the card at the chosen variant.
            var url = sw.getAttribute('data-swatch-url');
            if (url) {
              $$('[data-card-link]', card).forEach(function (a) { a.setAttribute('href', url); });
            }
          });
        });
      });
    }

    bind(document);
    document.addEventListener('covve:content', function (e) { bind(e.target || document); });
  })();

  // ======================================================================
  // Product page — variant picker, accordions, add to bag
  // ======================================================================

  (function product() {
    var page = $('[data-product-page]');
    if (!page) return;

    var form = $('[data-add-form]', page);
    var idInput = $('[data-variant-id]', page);
    var button = $('[data-add-button]', page);
    var label = $('[data-add-label]', page);
    var priceEl = $('[data-pdp-price]', page);
    var colourLabel = $('[data-colour-label]', page);
    var gallery = $('[data-pdp-gallery]', page);

    var variants = [];
    try {
      variants = JSON.parse($('[data-variant-data]', page).textContent);
    } catch (e) {
      console.warn('[covve] variant data unreadable', e);
    }

    function selected() {
      // One value per option position, in position order.
      var values = [];
      $$('[data-option-value]', page).forEach(function (input) {
        var pos = parseInt(input.getAttribute('data-option-position'), 10) - 1;
        var chosen = input.tagName === 'BUTTON'
          ? input.getAttribute('aria-checked') === 'true'
          : input.checked;
        if (chosen) values[pos] = input.getAttribute('data-value') || input.value;
      });
      return values;
    }

    function match(values) {
      return variants.filter(function (v) {
        return values.every(function (val, i) {
          return val === undefined || v.options[i] === val;
        });
      })[0];
    }

    function updateSoldOut(values) {
      // Grey out sizes that have no available variant for the chosen colour.
      $$('.size-grid input[data-option-value]', page).forEach(function (input) {
        var pos = parseInt(input.getAttribute('data-option-position'), 10) - 1;
        var probe = values.slice();
        probe[pos] = input.value;

        var available = variants.some(function (v) {
          return v.available && probe.every(function (val, i) {
            return val === undefined || v.options[i] === val;
          });
        });

        var lbl = page.querySelector('label[for="' + input.id + '"]');
        if (lbl) lbl.classList.toggle('size-btn--soldout', !available);
        input.toggleAttribute('data-soldout', !available);
      });
    }

    var userPicked = false;

    function apply() {
      var values = selected();
      var variant = match(values);

      updateSoldOut(values);

      // Keep the radiogroup a single tab stop, and label the current colour.
      $$('.swatches--lg [data-option-value]', page).forEach(function (sw) {
        var on = sw.getAttribute('aria-checked') === 'true';
        sw.setAttribute('tabindex', on ? '0' : '-1');
        if (on && colourLabel) colourLabel.textContent = sw.getAttribute('data-value');
      });

      if (!variant) {
        if (button) button.disabled = true;
        if (label) label.textContent = CFG.strings.unavailable;
        return;
      }

      if (idInput) idInput.value = variant.id;

      if (priceEl) {
        priceEl.innerHTML = (variant.compare_at && variant.compare_at > variant.price
          ? '<s>' + variant.compare_at_formatted + '</s> '
          : '') + variant.price_formatted;
      }

      if (button) button.disabled = !variant.available;
      if (label) {
        label.textContent = variant.available
          ? (CFG.strings.add_to_bag_price || '').replace('%%PRICE%%', variant.price_formatted)
          : CFG.strings.sold_out;
      }

      // Only react to a real choice — on first paint the server already
      // rendered the right variant, and scrolling or rewriting the URL then
      // would fight the browser's own restoration.
      if (!userPicked) return;

      // Bring the variant's own image into view.
      if (gallery && variant.media_id) {
        var shot = $('[data-media-id="' + variant.media_id + '"]', gallery);
        if (shot && shot.previousElementSibling) {
          shot.scrollIntoView({ block: 'nearest', behavior: 'smooth' });
        }
      }

      // Reflect the choice in the URL without reloading.
      if (variant.url && window.history && history.replaceState) {
        history.replaceState({}, '', variant.url);
      }
    }

    $$('[data-option-value]', page).forEach(function (input) {
      if (input.tagName === 'BUTTON') {
        on(input, 'click', function () {
          var pos = input.getAttribute('data-option-position');
          $$('[data-option-value][data-option-position="' + pos + '"]', page).forEach(function (o) {
            o.setAttribute('aria-checked', 'false');
          });
          input.setAttribute('aria-checked', 'true');
          userPicked = true;
          apply();
        });
        on(input, 'keydown', function (e) {
          if (e.key !== 'ArrowRight' && e.key !== 'ArrowLeft') return;
          e.preventDefault();
          var pos = input.getAttribute('data-option-position');
          var group = $$('[data-option-value][data-option-position="' + pos + '"]', page);
          var i = group.indexOf(input);
          var next = group[(i + (e.key === 'ArrowRight' ? 1 : -1) + group.length) % group.length];
          if (next) { next.click(); next.focus(); }
        });
      } else {
        on(input, 'change', function () {
          if (input.hasAttribute('data-soldout')) return;
          userPicked = true;
          apply();
        });
        // Block selection of a sold-out size without losing keyboard access.
        on(input, 'click', function (e) {
          if (input.hasAttribute('data-soldout')) e.preventDefault();
        });
      }
    });

    apply();

    // Accordions
    $$('[data-accordion]', page).forEach(function (btn) {
      on(btn, 'click', function () {
        var open = btn.getAttribute('aria-expanded') === 'true';
        btn.setAttribute('aria-expanded', open ? 'false' : 'true');
        var body = document.getElementById(btn.getAttribute('aria-controls'));
        if (body) body.hidden = open;
        var sign = $('[data-accordion-sign]', btn);
        if (sign) sign.textContent = open ? '+' : '−';
      });
    });

    // Add to bag
    on(form, 'submit', function (e) {
      if (!CFG.cart.drawer) return; // let the form post normally
      e.preventDefault();
      if (!idInput || button.disabled) return;

      var original = label ? label.textContent : '';
      button.disabled = true;

      cart.add(idInput.value, 1)
        .then(function () {
          if (label) label.textContent = CFG.strings.added;
          setTimeout(function () {
            if (label) label.textContent = original;
            button.disabled = false;
          }, 1600);
        })
        .catch(function (err) {
          console.warn('[covve] add to cart failed', err);
          if (label) label.textContent = err.message || CFG.strings.cart_error;
          setTimeout(function () {
            if (label) label.textContent = original;
            button.disabled = false;
          }, 2400);
        });
    });
  })();

  // ======================================================================
  // Collection — filters, sort, column count
  // ======================================================================

  (function collection() {
    var body = $('[data-coll-body]');
    if (!body) return;

    // Filters
    var toggle = $('[data-toggle-filters]');
    var filters = $('[data-filters-form]');

    if (toggle && filters) {
      on(toggle, 'click', function () {
        var open = filters.hidden;
        filters.hidden = !open;
        body.classList.toggle('filters-open', open);
        toggle.setAttribute('aria-expanded', String(open));
        var label = open ? CFG.strings.filter_close : CFG.strings.filter_open;
        if (label) toggle.textContent = label;
        try { localStorage.setItem('covve:filters', open ? '1' : '0'); } catch (e) {}
      });

      try {
        if (localStorage.getItem('covve:filters') === '1') toggle.click();
      } catch (e) {}

      // Submit on change so filtering feels immediate.
      on(filters, 'change', function () { filters.submit(); });
    }

    // Sort — cycle through the real sort options, as the mockup's button did.
    var sortBtn = $('[data-cycle-sort]');
    if (sortBtn) {
      on(sortBtn, 'click', function () {
        var options = (sortBtn.getAttribute('data-sort-options') || '').split(',').filter(Boolean);
        var current = sortBtn.getAttribute('data-sort-current');
        if (!options.length) return;
        var next = options[(options.indexOf(current) + 1) % options.length];
        var url = new URL(window.location.href);
        url.searchParams.set('sort_by', next);
        url.searchParams.delete('page');
        window.location.href = url.toString();
      });
    }

    // Column count
    var grid = $('[data-coll-grid]');
    var colButtons = $$('[data-set-cols]');
    if (grid && colButtons.length) {
      var setCols = function (n, persist) {
        grid.style.setProperty('--cols', n);
        colButtons.forEach(function (b) {
          b.setAttribute('aria-pressed', String(b.getAttribute('data-set-cols') === String(n)));
        });
        if (persist) {
          try { localStorage.setItem('covve:cols', String(n)); } catch (e) {}
        }
      };
      colButtons.forEach(function (b) {
        on(b, 'click', function () { setCols(b.getAttribute('data-set-cols'), true); });
      });
      try {
        var saved = localStorage.getItem('covve:cols');
        if (saved) setCols(saved, false);
      } catch (e) {}
    }
  })();

  // ======================================================================
  // Tabs
  // ======================================================================

  (function tabs() {
    $$('[data-tabs-section]').forEach(function (section) {
      var buttons = $$('[role="tab"]', section);
      var panels = $$('[role="tabpanel"]', section);
      if (buttons.length < 2) return;

      function select(i) {
        buttons.forEach(function (b, n) { b.setAttribute('aria-selected', String(n === i)); });
        panels.forEach(function (p, n) { p.hidden = n !== i; });
      }

      buttons.forEach(function (b, i) {
        on(b, 'click', function () { select(i); });
        on(b, 'keydown', function (e) {
          if (e.key !== 'ArrowRight' && e.key !== 'ArrowLeft') return;
          e.preventDefault();
          var next = (i + (e.key === 'ArrowRight' ? 1 : -1) + buttons.length) % buttons.length;
          buttons[next].focus();
          select(next);
        });
      });
    });
  })();

  // ======================================================================
  // Product recommendations
  // ======================================================================

  (function recommendations() {
    var el = $('[data-recommendations]');
    if (!el) return;
    var url = el.getAttribute('data-url');
    if (!url) return;

    fetch(url)
      .then(function (r) { return r.ok ? r.text() : Promise.reject(r.status); })
      .then(function (html) {
        var tmp = document.createElement('div');
        tmp.innerHTML = html;
        var fresh = $('[data-recommendations]', tmp);
        if (fresh && fresh.innerHTML.trim()) {
          el.innerHTML = fresh.innerHTML;
          el.dispatchEvent(new CustomEvent('covve:content', { bubbles: true }));
        }
      })
      .catch(function (err) { console.warn('[covve] recommendations failed', err); });
  })();

})();
